/**
 * The workflow editor.
 *
 * Three columns: the list (with each slug's in-force scope), the variant
 * editor, and version history. The editor's spine is FIRST-MATCH ORDER —
 * variants are tried top-down and the first whose conditions all hold wins —
 * so reordering is editing behaviour and the order is always visible.
 *
 * Two rules inherited from the store:
 *  - Saving creates an inactive version; putting it live is a second click.
 *  - Condition names come from the server's vocabulary. A misspelled name
 *    cannot be typed (conditions are picked, not typed) and would be refused
 *    by the API anyway — the vendor shipped variants that could never match,
 *    and this screen is where that bug became impossible.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, GitBranch, Plus, Trash2 } from 'lucide-react';
import { ApiError, apiGet, apiPost } from '../api/client';
import { useBrands } from '../api/admin';
import { Badge, Button, Card } from '../components/ui/primitives';
import { cn } from '../lib/cn';
import { formatDate } from '../lib/format';
import { diffWords } from '../lib/ticket/diff';

interface VariantDraft {
  position: number;
  title: string;
  requiredConditions: string[];
  botInstructions: string | null;
}

interface WorkflowListEntry {
  slug: string;
  title: string;
  scope: 'shared' | 'brand' | 'inactive';
  version: number | null;
  variantCount: number;
  isEmbedded: boolean;
  hasBrandRows: boolean;
}

interface WorkflowList {
  conditionNames: string[];
  workflows: WorkflowListEntry[];
}

interface WorkflowVersion {
  id: string;
  brandId: string | null;
  slug: string;
  title: string;
  whenToFollow: string | null;
  variants: VariantDraft[];
  isEmbedded: boolean;
  availableChannels: string[];
  version: number;
  active: boolean;
  createdAt: string;
  scope: 'shared' | 'brand';
}

interface WorkflowDetail {
  slug: string;
  conditionNames: string[];
  inForceId: string | null;
  versions: WorkflowVersion[];
}

export function WorkflowsPage() {
  const brands = useBrands();
  const [brandId, setBrandId] = useState('');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);

  const scopeQuery = brandId ? `?brandId=${brandId}` : '';
  const list = useQuery({
    queryKey: ['workflows', brandId || 'shared'],
    queryFn: () => apiGet<WorkflowList>(`/api/workflows${scopeQuery}`),
  });

  const slug = selectedSlug ?? list.data?.workflows[0]?.slug ?? null;

  if (list.isLoading) {
    return <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>;
  }

  return (
    <div className="flex h-full min-h-0">
      <div className="flex w-72 shrink-0 flex-col overflow-y-auto border-r border-line bg-surface">
        <div className="border-b border-line px-3 py-2.5">
          <label htmlFor="wf-brand" className="eyebrow mb-1 block text-ink-500">
            Editing for
          </label>
          <select
            id="wf-brand"
            value={brandId}
            onChange={(event) => setBrandId(event.target.value)}
            className="w-full rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink-900"
          >
            <option value="">Shared (every brand inherits)</option>
            {(brands.data ?? []).map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>
        </div>
        <ul>
          {(list.data?.workflows ?? []).map((entry) => (
            <li key={entry.slug}>
              <button
                type="button"
                onClick={() => setSelectedSlug(entry.slug)}
                aria-current={slug === entry.slug ? 'true' : undefined}
                className={cn(
                  'block w-full border-b border-line px-3 py-2 text-left transition-colors duration-fast',
                  slug === entry.slug ? 'bg-accent-50' : 'hover:bg-surface-sunken',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-xs font-medium text-ink-900">{entry.title}</span>
                  <Badge tone={entry.scope === 'brand' ? 'accent' : 'neutral'}>
                    {entry.scope === 'brand' ? 'override' : entry.scope}
                  </Badge>
                </div>
                <div className="mt-0.5 font-mono text-2xs text-ink-400">
                  {entry.slug} · {entry.variantCount} variant{entry.variantCount === 1 ? '' : 's'}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {slug ? (
        <WorkflowEditor key={`${slug}:${brandId}`} slug={slug} brandId={brandId || null} />
      ) : (
        <p className="flex-1 px-5 py-12 text-center text-sm text-ink-400">
          No workflows in this scope.
        </p>
      )}
    </div>
  );
}

/** Concatenates the human-readable body of a version, for the version diff. */
function versionText(version: WorkflowVersion): string {
  const parts = [version.title, version.whenToFollow ?? ''];
  for (const variant of version.variants ?? []) {
    parts.push(
      `[${variant.title} | ${variant.requiredConditions.join(', ')}] ${variant.botInstructions ?? ''}`,
    );
  }
  return parts.filter(Boolean).join('\n');
}

function WorkflowEditor({ slug, brandId }: { slug: string; brandId: string | null }) {
  const queryClient = useQueryClient();
  const scopeQuery = brandId ? `?brandId=${brandId}` : '';

  const detail = useQuery({
    queryKey: ['workflow', slug, brandId ?? 'shared'],
    queryFn: () => apiGet<WorkflowDetail>(`/api/workflows/${slug}${scopeQuery}`),
  });

  const inForce = useMemo(
    () => detail.data?.versions.find((v) => v.id === detail.data?.inForceId) ?? null,
    [detail.data],
  );

  // The editor drafts on top of one version — the in-force one by default.
  const [baseId, setBaseId] = useState<string | null>(null);
  const base = detail.data?.versions.find((v) => v.id === baseId) ?? inForce;

  const [title, setTitle] = useState('');
  const [whenToFollow, setWhenToFollow] = useState('');
  const [variants, setVariants] = useState<VariantDraft[]>([]);
  const [dirty, setDirty] = useState(false);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  // Load the base version into the draft whenever it changes and nothing has
  // been edited on top of it.
  useEffect(() => {
    if (!base || dirty) return;
    setTitle(base.title);
    setWhenToFollow(base.whenToFollow ?? '');
    setVariants(
      (base.variants ?? []).map((v, index) => ({
        position: index,
        title: v.title,
        requiredConditions: v.requiredConditions ?? [],
        botInstructions: v.botInstructions ?? null,
      })),
    );
  }, [base, dirty]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['workflow', slug] });
    await queryClient.invalidateQueries({ queryKey: ['workflows'] });
  };

  const save = useMutation({
    mutationFn: () =>
      apiPost<{ id: string; version: number }>('/api/workflows', {
        slug,
        brandId,
        title: title.trim(),
        whenToFollow: whenToFollow.trim() || null,
        variants: variants.map((v, index) => ({ ...v, position: index })),
        isEmbedded: base?.isEmbedded ?? false,
        availableChannels: base?.availableChannels ?? [],
      }),
    onSuccess: async (created) => {
      setOutcome(`Saved as v${created.version} — inactive until you activate it.`);
      setProblem(null);
      setDirty(false);
      setBaseId(created.id);
      await refresh();
    },
    onError: (error: unknown) => {
      setProblem(error instanceof ApiError ? error.message : 'Could not save.');
      setOutcome(null);
    },
  });

  const activate = useMutation({
    mutationFn: (id: string) => apiPost(`/api/workflows/${id}/activate`),
    onSuccess: async () => {
      setOutcome('Activated — this version now composes into prompts.');
      setProblem(null);
      await refresh();
    },
    onError: (error: unknown) => {
      setProblem(error instanceof ApiError ? error.message : 'Could not activate.');
    },
  });

  const touch = (fn: () => void) => {
    setDirty(true);
    setOutcome(null);
    fn();
  };

  const moveVariant = (index: number, delta: number) => {
    setDirty(true);
    setVariants((current) => {
      const next = [...current];
      const target = index + delta;
      if (target < 0 || target >= next.length) return current;
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item!);
      return next;
    });
  };

  if (detail.isLoading) {
    return <p className="flex-1 px-5 py-12 text-center text-sm text-ink-400">Loading…</p>;
  }
  if (!detail.data || !base) {
    return (
      <p className="flex-1 px-5 py-12 text-center text-sm text-ink-400">
        Nothing is configured for this slug in this scope yet.
      </p>
    );
  }

  const conditionOptions = detail.data.conditionNames;

  return (
    <>
      <div className="min-w-0 flex-1 overflow-y-auto bg-paper p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <GitBranch size={15} strokeWidth={1.75} className="text-ink-400" aria-hidden />
          <input
            value={title}
            onChange={(event) => {
              setDirty(true);
              setTitle(event.target.value);
            }}
            aria-label="Workflow title"
            className="min-w-[220px] flex-1 rounded border border-line bg-surface px-2.5 py-1.5 text-sm font-semibold text-ink-900"
          />
          {base.active ? <Badge tone="fact">v{base.version} active</Badge> : <Badge>v{base.version} draft</Badge>}
          <Badge tone={base.scope === 'brand' ? 'accent' : 'neutral'}>{base.scope}</Badge>
        </div>

        <textarea
          value={whenToFollow}
          onChange={(event) => {
            setDirty(true);
            setWhenToFollow(event.target.value);
          }}
          rows={2}
          placeholder="When to follow this workflow…"
          aria-label="When to follow"
          className="mb-3 w-full rounded border border-line bg-surface px-2.5 py-1.5 text-sm text-ink-700"
        />

        <p className="mb-2 text-xs text-ink-500">
          Variants are tried top-down; the <b>first whose conditions all hold wins</b>. An empty
          condition list means &ldquo;always matches&rdquo; — anything after it can never run.
        </p>

        <div className="space-y-2.5">
          {variants.map((variant, index) => (
            <Card key={index}>
              <div className="flex items-center gap-2 border-b border-line px-3 py-2">
                <span className="tabular text-xs font-semibold text-ink-400">{index + 1}</span>
                <input
                  value={variant.title}
                  onChange={(event) =>
                    touch(
                      () =>
                        setVariants((current) =>
                          current.map((v, i) => (i === index ? { ...v, title: event.target.value } : v)),
                        ),
                    )
                  }
                  aria-label={`Variant ${index + 1} title`}
                  className="min-w-0 flex-1 rounded border border-line bg-surface px-2 py-1 text-xs font-medium text-ink-900"
                />
                <Button size="sm" variant="ghost" aria-label="Move up" disabled={index === 0} onClick={() => moveVariant(index, -1)}>
                  <ArrowUp size={13} strokeWidth={2} aria-hidden />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Move down"
                  disabled={index === variants.length - 1}
                  onClick={() => moveVariant(index, 1)}
                >
                  <ArrowDown size={13} strokeWidth={2} aria-hidden />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Delete variant"
                  onClick={() =>
                    touch(() => setVariants((current) => current.filter((_, i) => i !== index)))
                  }
                >
                  <Trash2 size={13} strokeWidth={2} aria-hidden />
                </Button>
              </div>

              <div className="px-3 py-2">
                <div className="mb-1.5 flex flex-wrap gap-1">
                  {conditionOptions.map((name) => {
                    const on = variant.requiredConditions.includes(name);
                    return (
                      <button
                        key={name}
                        type="button"
                        aria-pressed={on}
                        onClick={() =>
                          touch(
                            () =>
                              setVariants((current) =>
                                current.map((v, i) =>
                                  i === index
                                    ? {
                                        ...v,
                                        requiredConditions: on
                                          ? v.requiredConditions.filter((c) => c !== name)
                                          : [...v.requiredConditions, name],
                                      }
                                    : v,
                                ),
                              ),
                          )
                        }
                        className={cn(
                          'rounded-sm px-1.5 py-0.5 font-mono text-2xs transition-colors duration-fast',
                          on
                            ? 'bg-accent-50 font-semibold text-accent-700 shadow-hairline'
                            : 'bg-surface-sunken text-ink-400 shadow-hairline hover:text-ink-700',
                        )}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
                <textarea
                  value={variant.botInstructions ?? ''}
                  onChange={(event) =>
                    touch(
                      () =>
                        setVariants((current) =>
                          current.map((v, i) =>
                            i === index ? { ...v, botInstructions: event.target.value || null } : v,
                          ),
                        ),
                    )
                  }
                  rows={3}
                  placeholder="Instructions for this variant…"
                  aria-label={`Variant ${index + 1} instructions`}
                  className="w-full rounded border border-line bg-surface px-2 py-1.5 text-xs text-ink-700"
                />
              </div>
            </Card>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            onClick={() =>
              touch(
                () =>
                  setVariants((current) => [
                    ...current,
                    { position: current.length, title: '', requiredConditions: [], botInstructions: null },
                  ]),
              )
            }
          >
            <Plus size={13} strokeWidth={2} aria-hidden />
            Add variant
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save as new version'}
          </Button>
          <span className="text-2xs text-ink-400">Saving does not publish — activate it from the version list.</span>
        </div>

        {outcome ? <p className="mt-2 text-xs text-ok-700">{outcome}</p> : null}
        {problem ? <p className="mt-2 whitespace-pre-wrap text-xs text-stop-700">{problem}</p> : null}
      </div>

      <aside className="w-72 shrink-0 overflow-y-auto border-l border-line bg-surface p-3">
        <div className="eyebrow mb-2">Versions</div>
        <ul className="space-y-1.5">
          {detail.data.versions.map((version) => (
            <li key={version.id} className="rounded border border-line px-2.5 py-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-ink-900">v{version.version}</span>
                <Badge tone={version.scope === 'brand' ? 'accent' : 'neutral'}>{version.scope}</Badge>
                {version.id === detail.data?.inForceId ? <Badge tone="fact">in force</Badge> : null}
                <span className="ml-auto text-2xs text-ink-400">{formatDate(version.createdAt)}</span>
              </div>
              <div className="mt-1.5 flex gap-1.5">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setBaseId(version.id);
                    setDirty(false);
                    setOutcome(null);
                  }}
                >
                  Open
                </Button>
                {version.id !== detail.data?.inForceId && !version.active ? (
                  <Button size="sm" variant="ghost" onClick={() => activate.mutate(version.id)}>
                    Activate
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>

        {base && inForce && base.id !== inForce.id ? (
          <VersionDiff from={inForce} to={base} />
        ) : null}
      </aside>
    </>
  );
}

/** What this version changes against the in-force one, as a word diff. */
function VersionDiff({ from, to }: { from: WorkflowVersion; to: WorkflowVersion }) {
  const tokens = useMemo(() => diffWords(versionText(from), versionText(to)), [from, to]);
  const changed = tokens.some((token) => token.kind !== 'same');

  return (
    <div className="mt-3">
      <div className="eyebrow mb-1.5">
        v{to.version} vs in-force v{from.version}
      </div>
      <div className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded border border-line bg-surface-sunken px-2.5 py-2 text-2xs leading-relaxed text-ink-700">
        {changed ? (
          tokens.map((token, index) =>
            token.kind === 'same' ? (
              <span key={index}>{token.text}</span>
            ) : (
              <span
                key={index}
                className={cn(
                  token.kind === 'del' && 'bg-stop-100 text-stop-700 line-through',
                  token.kind === 'add' && 'bg-ok-100 text-ok-700',
                )}
              >
                {token.text}
              </span>
            ),
          )
        ) : (
          <span className="text-ink-400">Identical text.</span>
        )}
      </div>
    </div>
  );
}
